import { InstructorAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/InstructorAssignmentCubit'
import { InstructorSectionCubit } from '@breakoutlearning/firebase-repository/cubits/InstructorSectionCubit'
import {
  AssignmentState,
  OrganizationInvoiceStatus,
  SectionState,
} from '@breakoutlearning/firebase-repository/types'
import { PageTitle } from 'components/PageTitle'
import { FullPageSpinner } from 'components/Spinner'
import { useRepository } from 'hooks/auth'
import { useCubitBuilder } from 'hooks/cubits'
import {
  InstructorAssignmentCubitProvider,
  useInstructorAssignmentCubit,
} from 'hooks/cubits/instructorAssignment'
import {
  InstructorSectionCubitProvider,
  useInstructorSectionCubit,
} from 'hooks/cubits/instructorSection'
import { useBreakoutUser } from 'hooks/profile'
import { useRootStore } from 'hooks/rootStore'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { AssignmentConfiguration } from './AssignmentConfiguration'
import { ConfigurationComplete } from './ConfigurationComplete'
import { BillingConfiguration } from './BillingConfiguration/BillingConfiguration'
import {
  SectionActivationScreen,
  SectionDatesConfiguration,
} from './SectionConfiguration'
import { InvalidExpirationPage } from './InvalidExpirationPage'

import { ErrorBoundary } from 'react-error-boundary'
import { LtiErrorView } from './LtiErrorPage'
import { useLTIMode } from 'hooks/use-lti-mode'
import { BrightspaceConfirmationPage } from './BrightspaceConfirmationPage'
import { CanvasConfirmationPage } from './CanvasConfirmationPage'
import { useTranslationTyped } from 'i18n/i18n'

function LtiErrorBoundary(props: React.PropsWithChildren) {
  return <ErrorBoundary FallbackComponent={ErrorFallback} {...props} />
}

function ErrorFallback({ error }: { error: Error }) {
  return <LtiErrorView errorText={error.message} />
}

export const LtiConfigurationPage = observer(function HomePage() {
  const store = useRootStore()
  const params = store.router.params
  const sectionId = params?.sectionId as string | undefined
  const assignmentId = params?.assignmentId as string | undefined
  const { tt } = useTranslationTyped()

  if (!sectionId || !assignmentId) {
    return <div>{tt.lti.configuration.invalid_url()}</div>
  }

  return (
    <LtiErrorBoundary>
      <PageTitle title={tt.lti.configuration.page_title()} />
      <InstructorConfigurationPage
        sectionId={sectionId}
        assignmentId={assignmentId}
      />
    </LtiErrorBoundary>
  )
})

function InstructorConfigurationPage({
  sectionId,
  assignmentId,
}: {
  sectionId: string
  assignmentId: string
}) {
  const repository = useRepository()
  const user = useBreakoutUser()

  const sectionCubit = useCubitBuilder(() => {
    return new InstructorSectionCubit(repository, sectionId)
  }, [repository, sectionId])

  const assignmentCubit = useCubitBuilder(() => {
    return new InstructorAssignmentCubit(repository, {
      sectionId,
      assignmentId,
    })
  }, [repository, sectionId, assignmentId, user])

  return (
    <InstructorSectionCubitProvider value={sectionCubit}>
      <InstructorAssignmentCubitProvider value={assignmentCubit}>
        <div className="lti-configuration flex min-h-full w-full">
          <PageBody />
        </div>
      </InstructorAssignmentCubitProvider>
    </InstructorSectionCubitProvider>
  )
}

const PageBody = observer(function PageBody() {
  const { ltiMode } = useLTIMode()
  const user = useBreakoutUser()
  const cubit = useInstructorAssignmentCubit()
  const sectionCubit = useInstructorSectionCubit()
  const section = sectionCubit.section
  const sectionHasDates = Boolean(
    section.data.sectionStartDate && section.data.sectionEndDate
  )
  const sectionState = cubit.section.data?.sectionState
  const assignmentState = cubit.assignment.data?.assignmentState
  // this controls whether the user wants to proceed to the invoicing step
  const [wantsInvoicing, setWantsInvoicing] = useState(false)

  // Always bypass explicit assignment confirmation (modal is no longer used)
  const bypassExplicitAssignmentConfirmation = true

  const pendingSectionRequest = sectionCubit.pendingSectionRequest

  const sectionPaymentMethodUndefined =
    sectionCubit.section.data?.sectionPaymentMethod === undefined

  useEffect(() => {
    if (!sectionCubit.organizationLoaded) return
    if (!sectionCubit.section.isLoaded) return

    sectionCubit.section.data.expectedStudents === undefined
    if (sectionCubit.sectionOrganization) {
      const orgHasInvoices =
        sectionCubit.sectionOrganization.data.organizationInvoiceStatus ===
          OrganizationInvoiceStatus.adminApproved &&
        sectionPaymentMethodUndefined
      setWantsInvoicing(orgHasInvoices)
    }
  }, [
    sectionCubit.sectionOrganization,
    sectionCubit.organizationLoaded,
    sectionCubit.section.isLoaded,
    sectionCubit.section.data.expectedStudents,
    sectionPaymentMethodUndefined,
  ])

  const sectionOrganization = sectionCubit.sectionOrganization
  // const sectionHasPromotions = sectionCubit.promotions.models.length > 0
  const isLoading =
    user.userPromotions.isLoading ||
    user.defaultSystemProducts.isLoading ||
    cubit.section.isLoading ||
    cubit.assignment.isLoading ||
    sectionCubit.section.isLoading ||
    sectionCubit.sectionProduct.isLoading ||
    sectionCubit.sectionInvoiceRequests.isLoading ||
    sectionCubit.organizationLoading ||
    sectionCubit.organizationCustomProducts.isLoading ||
    !sectionOrganization

  if (isLoading) {
    return <FullPageSpinner />
  }

  if (sectionState === SectionState.unconfigured && !sectionHasDates) {
    // ask to provide dates
    return <SectionDatesConfiguration />
  }

  if (sectionState === SectionState.unconfigured) {
    return <SectionActivationScreen />
  }

  // if we have an approval, we skip invoicing altogether
  const skipInvoicing =
    sectionOrganization.data.organizationInvoiceStatus ===
      OrganizationInvoiceStatus.adminApproved &&
    sectionCubit.approvedSectionRequest

  const showInvoicing =
    !skipInvoicing &&
    (wantsInvoicing || pendingSectionRequest || sectionPaymentMethodUndefined)

  if (sectionState === SectionState.notStarted && showInvoicing) {
    return (
      <BillingConfiguration
        sectionOrganization={sectionOrganization}
        invoiceStatus={sectionOrganization.data.organizationInvoiceStatus}
        setWantsInvoicing={setWantsInvoicing}
        sectionRequest={pendingSectionRequest}
      />
    )
  }

  const expiresAtMissing = !cubit.assignment.data?.expiresAt

  // brightspace ends here - it has it's own page with errors and configuration
  if (ltiMode === 'brightspace') {
    return <BrightspaceConfirmationPage />
  }

  // use simple config = bypass confirmation
  if (ltiMode === 'canvas' && bypassExplicitAssignmentConfirmation) {
    return <CanvasConfirmationPage />
  }

  if (expiresAtMissing) {
    return <InvalidExpirationPage />
  }

  if (assignmentState === AssignmentState.unconfigured) {
    return <AssignmentConfiguration />
  }

  return <ConfigurationComplete />
})
